import { config } from "@/shared/lib/config";
import {
  addMinutesToTimeHhMm,
  parseTimeToDate,
  toTimeHHmmss,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  nearestSlotDurationMinutes,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  isCanonicalQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  FormDateField,
  FormInput,
  FormRoot,
  FormTextarea,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { differenceInMinutes } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type {
  ClassSessionDTO,
  RoomConflictResultDTO,
  UpdateClassSessionDTO,
} from "../../domain/contracts/sessions";
import { classesApi } from "../api/client";
import { useFindAlternativeRooms, useUpdateClassSession } from "../api/queries";
import {
  instructorsFromLeadAssist,
  leadAssistFromSessionInstructors,
} from "../lib/session-instructor-form";
import { RoomConflictWarning } from "./RoomConflictWarning";

type RoomItem = { id: string; name: string };
type InstructorItem = { id: string; name: string };

function toTimeHHmm5(s: string | undefined): string {
  if (!s) return "09:00";
  if (s.length >= 8) return s.slice(0, 5);
  return s;
}

function toQuarterHhMmInitial(s: string | undefined): string {
  const { h, m } = parseQuarterClock(toTimeHHmm5(s));
  return formatQuarterHhMm(h, m);
}

function inferSlotDurationMinutesFromRange(
  start?: string,
  end?: string,
): PrivateLessonSlotDurationMinutes {
  if (!start?.trim() || !end?.trim()) return 60;
  try {
    const mins = differenceInMinutes(
      parseTimeToDate(toQuarterHhMmInitial(end)),
      parseTimeToDate(toQuarterHhMmInitial(start)),
    );
    if (mins <= 0) return 60;
    if (PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins as PrivateLessonSlotDurationMinutes;
    }
    return nearestSlotDurationMinutes(mins);
  } catch {
    return 60;
  }
}

const slotDurZ = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

const MoveSessionFormSchema = z
  .object({
    locationKind: z.enum(["studio", "off_site"]),
    offSiteLabel: z.string().optional(),
    engagementKind: z.enum(["standard", "partner_instructor_only"]),
    date: z.string().optional(),
    startTime: z.string().optional(),
    slotDurationMinutes: slotDurZ,
    roomId: z.string().optional(),
    leadInstructorId: z.string().min(1, "ຕ້ອງເລືອກຄູຫຼັກ"),
    assistInstructorIds: z.array(z.string()).max(3).optional(),
    reason: z.string().min(1, "ຕ້ອງໃສ່ເຫດຜົນການປ່ຽນແປງ"),
  })
  .superRefine((data, ctx) => {
    if (!data.startTime?.trim()) return;
    if (!isCanonicalQuarterHhMm(data.startTime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເວລາເລີ່ມຕົ້ນຕ້ອງເປັນນາທີ 00 / 15 / 30 / 45",
        path: ["startTime"],
      });
    }
    if (
      data.locationKind === "studio" &&
      (!data.roomId || data.roomId.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງເລືອກຫ້ອງເມື່ອສະຖານທີ່ເປັນສະຕູດິໂອ",
        path: ["roomId"],
      });
    }
    if (
      data.locationKind === "off_site" &&
      data.roomId &&
      data.roomId.trim().length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຄລາດສ໌ນອກສະຖານທີ່ບໍ່ໃຊ້ຫ້ອງສະຕູດິໂອ — ລ້າງຫ້ອງ",
        path: ["roomId"],
      });
    }
  });

type MoveSessionFormValues = z.infer<typeof MoveSessionFormSchema>;

type MoveSessionDialogProps = {
  session: ClassSessionDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function MoveSessionDialog({
  session,
  open,
  onOpenChange,
  onSuccess,
}: MoveSessionDialogProps) {
  const [conflictResult, setConflictResult] =
    useState<RoomConflictResultDTO | null>(null);
  const [isCheckingConflict, setIsCheckingConflict] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);

  const updateSession = useUpdateClassSession(session.id);
  const findAlternatives = useFindAlternativeRooms();

  const defaultDate = session.date ?? "";
  const defaultStartTime = toQuarterHhMmInitial(session.startTime);
  const defaultSlotDuration = inferSlotDurationMinutesFromRange(
    session.startTime,
    session.endTime,
  );
  const defaultLocationKind = session.locationKind;
  const defaultOffSiteLabel = session.offSiteLabel ?? "";
  const defaultEngagementKind = session.engagementKind;
  const defaultRoomId = session.roomId ?? "";
  const defaultRoles = leadAssistFromSessionInstructors(
    session.instructors,
    session.instructorIds,
  );
  const defaultLeadInstructorId = defaultRoles.leadInstructorId;
  const defaultAssistInstructorIds = defaultRoles.assistInstructorIds;
  const defaultInstructorIds = [
    defaultLeadInstructorId,
    ...defaultAssistInstructorIds,
  ].filter(Boolean);

  const methods = RHF.useForm<MoveSessionFormValues>({
    resolver: zodResolver(MoveSessionFormSchema),
    defaultValues: {
      locationKind: defaultLocationKind,
      offSiteLabel: defaultOffSiteLabel,
      engagementKind: defaultEngagementKind,
      date: defaultDate,
      startTime: defaultStartTime,
      slotDurationMinutes: defaultSlotDuration,
      roomId: defaultRoomId,
      leadInstructorId: defaultLeadInstructorId,
      assistInstructorIds: defaultAssistInstructorIds,
      reason: "",
    },
  });

  const locationKindWatch = methods.watch("locationKind");
  const roomIdWatch = methods.watch("roomId");
  const engagementKindWatch = methods.watch("engagementKind");
  const assistInstructorIdsWatch =
    methods.watch("assistInstructorIds") ?? [];

  useEffect(() => {
    if (
      engagementKindWatch === "partner_instructor_only" &&
      assistInstructorIdsWatch.length > 0
    ) {
      methods.setValue("assistInstructorIds", []);
    }
  }, [engagementKindWatch, assistInstructorIdsWatch.length, methods]);

  useEffect(() => {
    if (
      locationKindWatch === "off_site" &&
      roomIdWatch &&
      roomIdWatch.trim().length > 0
    ) {
      methods.setValue("roomId", "");
    }
  }, [locationKindWatch, roomIdWatch, methods]);

  const resolveStartEndHhmmss = (
    values: Partial<MoveSessionFormValues>,
  ): { start: string; end: string } => {
    const startHm = toQuarterHhMmInitial(
      values.startTime?.trim() || defaultStartTime,
    );
    const dur = values.slotDurationMinutes ?? defaultSlotDuration;
    const endHm = addMinutesToTimeHhMm(startHm, dur);
    return { start: toTimeHHmmss(startHm), end: toTimeHHmmss(endHm) };
  };

  useEffect(() => {
    if (open) {
      methods.reset({
        locationKind: defaultLocationKind,
        offSiteLabel: defaultOffSiteLabel,
        engagementKind: defaultEngagementKind,
        date: defaultDate,
        startTime: defaultStartTime,
        slotDurationMinutes: defaultSlotDuration,
        roomId: defaultRoomId,
        leadInstructorId: defaultLeadInstructorId,
        assistInstructorIds: defaultAssistInstructorIds,
        reason: "",
      });
      setConflictResult(null);
      setShowAlternatives(false);
    }
  }, [
    open,
    session.id,
    defaultDate,
    defaultStartTime,
    defaultSlotDuration,
    defaultLocationKind,
    defaultOffSiteLabel,
    defaultEngagementKind,
    defaultRoomId,
    defaultInstructorIds,
    methods,
  ]);

  const wtStart = methods.watch("startTime");
  const wtSlot = methods.watch("slotDurationMinutes");
  const computedEndPreview = useMemo(() => {
    try {
      const startHm = toQuarterHhMmInitial(wtStart?.trim() || defaultStartTime);
      const dur = wtSlot ?? defaultSlotDuration;
      return addMinutesToTimeHhMm(startHm, dur);
    } catch {
      return "—";
    }
  }, [wtStart, wtSlot, defaultStartTime, defaultSlotDuration]);

  const resolvedBaseline = resolveStartEndHhmmss({
    startTime: defaultStartTime,
    slotDurationMinutes: defaultSlotDuration,
  });

  const hasAnyChange = () => {
    const v = methods.getValues();
    const d = v.date ?? defaultDate;
    const lead = (v.leadInstructorId ?? "").trim();
    const assists = [...(v.assistInstructorIds ?? [])].filter(Boolean);
    const sameLead = lead === defaultLeadInstructorId;
    const sameAssists =
      assists.length === defaultAssistInstructorIds.length &&
      assists.every((id) => defaultAssistInstructorIds.includes(id)) &&
      defaultAssistInstructorIds.every((id) => assists.includes(id));
    const sameIds = sameLead && sameAssists;

    const curResolved = resolveStartEndHhmmss({
      startTime: v.startTime ?? defaultStartTime,
      slotDurationMinutes: v.slotDurationMinutes ?? defaultSlotDuration,
    });

    const curLabel =
      v.locationKind === "off_site" ? v.offSiteLabel?.trim() || "" : "";
    const baseLabel =
      defaultLocationKind === "off_site" ? defaultOffSiteLabel.trim() : "";
    const labelChanged = curLabel !== baseLabel;

    return (
      d !== defaultDate ||
      curResolved.start !== resolvedBaseline.start ||
      curResolved.end !== resolvedBaseline.end ||
      v.locationKind !== defaultLocationKind ||
      v.engagementKind !== defaultEngagementKind ||
      labelChanged ||
      (v.locationKind === "studio" &&
        (v.roomId ?? "").trim() !== defaultRoomId.trim()) ||
      !sameIds
    );
  };

  const checkConflict = async (values: MoveSessionFormValues) => {
    const useDate = values.date ?? defaultDate;
    const mergedTimes = resolveStartEndHhmmss({
      startTime: values.startTime ?? defaultStartTime,
      slotDurationMinutes: values.slotDurationMinutes ?? defaultSlotDuration,
    });

    const isOffSite = values.locationKind === "off_site";
    const useRoomId = isOffSite
      ? undefined
      : values.roomId?.trim() || defaultRoomId;

    if (!useDate || !mergedTimes.start || !mergedTimes.end) {
      return {
        hasConflict: false,
        conflictingSessions: [],
        busyInstructors: [],
      };
    }
    if (!isOffSite && (!useRoomId || useRoomId.trim().length === 0)) {
      return {
        hasConflict: false,
        conflictingSessions: [],
        busyInstructors: [],
      };
    }
    setIsCheckingConflict(true);
    try {
      const result = await classesApi.sessions.checkConflict({
        roomId: isOffSite ? undefined : useRoomId,
        date: useDate,
        startTime: mergedTimes.start,
        endTime: mergedTimes.end,
        sessionLocationKind: isOffSite ? "off_site" : "studio",
        instructorIds: (() => {
          const ids = [
            values.leadInstructorId,
            ...(values.assistInstructorIds ?? []),
          ].filter(Boolean);
          return ids.length ? ids : undefined;
        })(),
        excludeSessionId: session.id,
      });
      return result;
    } catch {
      return {
        hasConflict: false,
        conflictingSessions: [],
        busyInstructors: [],
      };
    } finally {
      setIsCheckingConflict(false);
    }
  };

  const handleSubmit = async (values: MoveSessionFormValues) => {
    if (!hasAnyChange()) {
      methods.setError("reason", {
        message: "ຕ້ອງມີການປ່ຽນແປງຢ່າງໜ້ອຍ 1 ລາຍການ",
      });
      return;
    }

    const useDate = values.date ?? defaultDate;
    const cand = resolveStartEndHhmmss({
      startTime: values.startTime ?? defaultStartTime,
      slotDurationMinutes: values.slotDurationMinutes ?? defaultSlotDuration,
    });
    const sessionStart = session.startTime ?? "";
    const sessionEnd = session.endTime ?? "";

    const lead = (values.leadInstructorId ?? "").trim();
    const assists =
      values.engagementKind === "partner_instructor_only"
        ? []
        : [...(values.assistInstructorIds ?? [])].filter(Boolean);
    const sameLead = lead === defaultLeadInstructorId;
    const sameAssists =
      assists.length === defaultAssistInstructorIds.length &&
      assists.every((id) => defaultAssistInstructorIds.includes(id)) &&
      defaultAssistInstructorIds.every((id) => assists.includes(id));
    const useInstructors =
      !sameLead || !sameAssists
        ? instructorsFromLeadAssist(lead, assists)
        : undefined;

    const result = await checkConflict({
      ...values,
      date: useDate,
      roomId:
        values.locationKind === "studio"
          ? (values.roomId ?? defaultRoomId)
          : "",
    });

    if (result.hasConflict) {
      setConflictResult(result);
      const suggestRooms =
        values.locationKind !== "off_site" &&
        result.conflictingSessions.length > 0;
      if (suggestRooms) {
        try {
          await findAlternatives.mutateAsync({
            date: useDate,
            startTime: cand.start,
            endTime: cand.end,
            excludeRoomId: values.roomId ?? defaultRoomId,
          });
          setShowAlternatives(true);
        } catch {
          // ignore
        }
      }
      return;
    }

    setConflictResult(null);
    setShowAlternatives(false);

    const payload: UpdateClassSessionDTO = {
      changeReason: values.reason,
    };
    if (values.date !== undefined && values.date !== defaultDate)
      payload.date = values.date;
    if (cand.start && cand.start !== sessionStart)
      payload.startTime = cand.start;
    if (cand.end && cand.end !== sessionEnd) payload.endTime = cand.end;

    if (values.locationKind !== defaultLocationKind) {
      payload.locationKind = values.locationKind;
    }

    const newOffLabel =
      values.locationKind === "off_site"
        ? values.offSiteLabel?.trim() || null
        : null;
    const oldOffLabel = session.offSiteLabel?.trim() || null;
    if (newOffLabel !== oldOffLabel) {
      payload.offSiteLabel = newOffLabel;
    }

    if (values.engagementKind !== defaultEngagementKind) {
      payload.engagementKind = values.engagementKind;
    }

    if (values.locationKind === "off_site") {
      if (
        defaultLocationKind === "studio" ||
        (session.roomId ?? "").length > 0
      ) {
        payload.roomId = null;
      }
    } else {
      const rid = values.roomId?.trim() ?? "";
      if (rid !== (session.roomId ?? "")) {
        payload.roomId = rid;
      }
    }

    if (useInstructors != null) payload.instructors = useInstructors;

    try {
      await updateSession.mutateAsync(payload);
      onSuccess?.();
      onOpenChange(false);
      methods.reset({
        locationKind: defaultLocationKind,
        offSiteLabel: defaultOffSiteLabel,
        engagementKind: defaultEngagementKind,
        date: defaultDate,
        startTime: defaultStartTime,
        slotDurationMinutes: defaultSlotDuration,
        roomId: defaultRoomId,
        instructorIds: defaultInstructorIds,
        reason: "",
      });
    } catch {
      // Error handled by mutation
    }
  };

  const handleSelectAlternativeRoom = (altRoomId: string) => {
    methods.setValue("roomId", altRoomId);
    setShowAlternatives(false);
    setConflictResult(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setConflictResult(null);
      setShowAlternatives(false);
    }
    onOpenChange(next);
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="ຍ້າຍຊົ່ວໂມງຮຽນ"
      size="md"
    >
      <FormRoot methods={methods} onSubmit={handleSubmit} className="space-y-4">
        <p className="text-muted-foreground text-sm">
          ປ່ຽນແປງວັນທີ, ເວລາ, ສະຖານທີ່, ຫ້ອງ (ສະຕູດິໂອ), ຄູຜູ້ສອນ. ຕ້ອງໃສ່ເຫດຜົນການປ່ຽນແປງ.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <RHF.Controller
            name="locationKind"
            control={methods.control}
            render={({ field }) => (
              <div className="space-y-2">
                <div className="font-medium text-sm">ປະເພດສະຖານທີ່</div>
                <SimpleSelect
                  value={field.value}
                  onValueChange={(v) =>
                    field.onChange(v as "studio" | "off_site")
                  }
                  options={[
                    { value: "studio", label: "ສະຕູດິໂອ" },
                    { value: "off_site", label: "ນອກສະຖານທີ່" },
                  ]}
                  className="h-9 w-full"
                />
              </div>
            )}
          />
          {locationKindWatch === "off_site" ? (
            <FormInput
              name="offSiteLabel"
              label="ຊື່ສະຖານທີ່ (ທາງເລືອກ)"
              placeholder="ຕົວຢ່າງ: ໂຮງຮຽນ ມປລ"
            />
          ) : (
            <div className="hidden sm:block" aria-hidden />
          )}
          <RHF.Controller
            name="engagementKind"
            control={methods.control}
            render={({ field }) => (
              <div className="space-y-2 sm:col-span-2">
                <div className="font-medium text-sm">ປະເພດໂຄງການ</div>
                <SimpleSelect
                  value={field.value}
                  onValueChange={(v) =>
                    field.onChange(v as "standard" | "partner_instructor_only")
                  }
                  options={[
                    { value: "standard", label: "ມາດຕະຖານ" },
                    {
                      value: "partner_instructor_only",
                      label: "Partner (instructor-only)",
                    },
                  ]}
                  className="h-9 w-full"
                />
              </div>
            )}
          />
        </div>

        <FormDateField name="date" label="ວັນທີ" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <div className="font-medium text-sm leading-none">ເວລາເລີ່ມຕົ້ນ</div>
            <RHF.Controller
              name="startTime"
              control={methods.control}
              render={({ field }) => (
                <QuarterHourMinuteSelect
                  value={field.value ?? defaultStartTime}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <div className="font-medium text-sm leading-none">
              ຄວາມຍາວ (ນາທີ)
            </div>
            <RHF.Controller
              name="slotDurationMinutes"
              control={methods.control}
              render={({ field }) => {
                const raw = Number(field.value);
                const safe = PRIVATE_LESSON_SLOT_DURATION_SET.has(raw)
                  ? raw
                  : defaultSlotDuration;
                return (
                  <SimpleSelect
                    value={String(safe)}
                    onValueChange={(val) => {
                      const m = Number(val);
                      if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m)) return;
                      field.onChange(m);
                    }}
                    options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                    className="h-9 w-full"
                    placeholder="ເລືອກຄວາມຍາວ"
                  />
                );
              }}
            />
          </div>
          <div className="space-y-2">
            <div className="font-medium text-muted-foreground text-sm leading-none">
              ເວລາສິ້ນສຸດ <span className="text-xs">(ຄຳນວນ)</span>
            </div>
            <div className="flex h-9 w-full items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
              {computedEndPreview}
            </div>
          </div>
        </div>

        {locationKindWatch === "studio" ? (
          <FormInfiniteCombobox<RoomItem>
            name="roomId"
            label="ຫ້ອງ"
            placeholder="ເລືອກຫ້ອງ"
            requiredMark
            queryKey={["master", "rooms", "lookup", "move"]}
            queryFn={({ search, pageParam }) =>
              fetchLookupForInfinite<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                { search, pageParam },
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<RoomItem>(
                `${config.apiUrl}/master/rooms/lookup`,
                id,
              ).then((item) => item ?? null)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
          />
        ) : (
          <div className="rounded-md border border-dashed bg-muted/30 p-3 text-muted-foreground text-sm">
            ຄລາດສ໌ນອກສະຖານທີ່ບໍ່ຜູກຫ້ອງສະຕູດິໂອ
          </div>
        )}

        <FormInfiniteCombobox<InstructorItem>
          name="leadInstructorId"
          label="ຄູຫຼັກ"
          placeholder="ເລືອກຄູຫຼັກ"
          requiredMark
          queryKey={["master", "instructors", "lookup", "move-lead"]}
          queryFn={({ search, pageParam }) =>
            fetchLookupForInfinite<InstructorItem>(
              `${config.apiUrl}/master/instructors/lookup`,
              { search, pageParam },
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<InstructorItem>(
              `${config.apiUrl}/master/instructors/lookup`,
              id,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          clearable
        />

        {engagementKindWatch !== "partner_instructor_only" ? (
          <div className="space-y-1">
            <FormInfiniteMultiCombobox<InstructorItem>
              name="assistInstructorIds"
              label="ຄູຊ່ວຍ (TA)"
              placeholder="ເລືອກຄູຊ່ວຍ ສູງສຸດ 3 ຄົນ"
              queryKey={["master", "instructors", "lookup", "move-assist"]}
              queryFn={({ search, pageParam }) =>
                fetchLookupForInfinite<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  { search, pageParam },
                )
              }
              preloadQueryFn={(ids) =>
                hydrateLookupItems<InstructorItem>(
                  `${config.apiUrl}/master/instructors/lookup`,
                  ids,
                )
              }
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
            />
            <p className="text-muted-foreground text-xs">
              ຄິດຄ່າຈ້າງຕໍ່ຄັ້ງ · ສູງສຸດ 3 ຄົນ
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            ຄລາດສ໌ Partner ໃຊ້ຄູຫຼັກຄົນດຽວ — ບໍ່ມີຄູຊ່ວຍ (TA)
          </p>
        )}

        <FormTextarea
          name="reason"
          label="ເຫດຜົນການປ່ຽນແປງ"
          placeholder="ປ້ອນເຫດຜົນ (ບັງຄັບ)"
          rows={2}
          requiredMark
        />

        {conflictResult?.hasConflict && (
          <div className="space-y-2">
            <RoomConflictWarning
              conflictResult={conflictResult}
              message="ຫ້ອງກຳລັງຖືກໃຊ້ງານໃນເວລານີ້. ກະລຸນາເລືອກຫ້ອງອື່ນຫຼືເວລາອື່ນ."
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100"
              onClick={async () => {
                const v = methods.getValues();
                const { start, end } = resolveStartEndHhmmss(v);
                await findAlternatives.mutateAsync({
                  date: v.date ?? defaultDate,
                  startTime: start,
                  endTime: end,
                  excludeRoomId: v.roomId ?? defaultRoomId,
                });
                setShowAlternatives(true);
              }}
              disabled={
                findAlternatives.isPending ||
                locationKindWatch === "off_site" ||
                (conflictResult?.conflictingSessions?.length ?? 0) === 0
              }
            >
              {findAlternatives.isPending ? "ກຳລັງຄົ້ນຫາ..." : "ແນະນຳຫ້ອງທາງເລືອກ"}
            </Button>
            {showAlternatives &&
              findAlternatives.data?.rooms &&
              findAlternatives.data.rooms.length > 0 && (
                <div className="space-y-1">
                  <p className="font-semibold text-sm">ຫ້ອງທີ່ມີຢູ່:</p>
                  {findAlternatives.data.rooms.map((r) => (
                    <Button
                      key={r.id}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleSelectAlternativeRoom(r.id)}
                    >
                      {r.name}
                      {r.capacity != null ? ` (${r.capacity} ຄົນ)` : ""}
                    </Button>
                  ))}
                </div>
              )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={updateSession.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="submit"
            disabled={updateSession.isPending || isCheckingConflict}
          >
            {isCheckingConflict
              ? "ກຳລັງກວດສອບ..."
              : updateSession.isPending
                ? "ກຳລັງບັນທຶກ..."
                : "ບັນທຶກ"}
          </Button>
        </div>
      </FormRoot>
    </Modal>
  );
}
